<template>
  <div class="post__collage-wrap">
    <div
      v-if="hasGalleryContext"
      :class="{ 'post__gallery-trigger-wrap--two-photos': props.images.length === 2 }"
      class="post__gallery-trigger-wrap"
    >
      <GalleryTrigger
        class="post__gallery-trigger"
        :preview-type="GalleryPreviewType.EXPERIENCE_SLIDER_GALLERY"
        :gallery-photos-list="galleryPhotosList"
        :more-photos-count="images.length"
        :trigger-max-photos-count="triggerMaxPhotosInTrigger"
        :title="galleryTitle"
        :gallery-origin="galleryOrigin"
        :sogu-url="soguUrl"
        @on-trigger-click="decreaseZIndex"
        @on-close-gallery-slider="increaseZIndex"
        @on-close-gallery-list="increaseZIndex"
      />
    </div>
    <div v-else class="post__collage">
      <div v-for="image in images" :key="image.src" class="post__collage-item">
        <img loading="lazy" :src="image.src" :alt="image.alt" />
      </div>
    </div>
    <div v-if="caption && caption.length > 0" class="post__collage-text-wrapper">
      <div class="post__collage-text">
        <ArticleContentRenderer :content="caption" :preloaded-data="preloadedData" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  GalleryTrigger,
  GalleryOriginType,
  GalleryPreviewType,
  type IGalleryTriggerProps,
} from '@experience/gallery-slider';
import { computed } from 'vue';

import { getRuntimeConfig } from '@/shared/config/runtime';
import { useHeaderZIndex } from '@/shared/lib/headerZIndex';

import ArticleContentRenderer from './ArticleContentRenderer.vue';

import type { PreloadedData } from './lib/articleComponents';
import type { ArticleContent } from './models/ArticleContent';

export interface Props {
  /**
   * Текст заголовка (подписи).
   */
  caption: ArticleContent;

  /**
   * Список изображений.
   */
  images: Array<{ src: string; alt: string }>;

  preloadedData: PreloadedData;
}

const props = defineProps<Props>();
const { decreaseZIndex, increaseZIndex } = useHeaderZIndex();

type GallerySlide = IGalleryTriggerProps['galleryPhotosList'][number];

const triggerMaxPhotosInTrigger = 3;

const soguUrl = getRuntimeConfig().VITE_RUNTIME_SOGU_SERVICE_URL;

const articlePost = computed(() => props.preloadedData.articlePost);

const hasGalleryContext = computed(
  () => articlePost.value != null && props.images.length > 0 && articlePost.value.id != null,
);

const galleryTitle = computed(() => articlePost.value?.title ?? '');

const galleryOrigin = computed(() => {
  const post = articlePost.value;
  if (post == null) {
    return {
      id: 0,
      type: GalleryOriginType.EXPERIENCE,
      title: '',
      description: '',
      number: 1,
    };
  }
  return {
    id: post.id,
    type: GalleryOriginType.EXPERIENCE,
    title: galleryTitle.value,
    description: '',
    number: 1,
  };
});

const galleryPhotosList = computed((): GallerySlide[] => {
  const post = articlePost.value;
  if (post == null) {
    return [];
  }
  return props.images.map((image, imageIndex) => ({
    id: `content-image-${post.id}-${imageIndex}`,
    index: imageIndex,
    originId: post.id,
    originType: GalleryOriginType.EXP_PLAN,
    alt: image.alt || galleryTitle.value,
    srcPhoto: image.src,
    thumbPhoto: image.src,
  }));
});
</script>

<style lang="scss" scoped>
.post__collage-wrap {
  max-width: var(--width-content);
  margin: 32px 0 0;

  :deep(.gallery-trigger__count) {
    @include base-800-800-regular;

    @include mobile {
      @include base-500-400-regular;
    }
  }

  /* stylelint-disable selector-class-pattern -- имена классов приходят из gallery-slider */

  /**
     * Пакет задаёт aspect-ratio 2/1 на list-opener — для журнала убираем.
     */
  :deep(.post__gallery-trigger-wrap .gallery-trigger.gallery-trigger_list-opener) {
    aspect-ratio: unset;
    margin-top: 0;
  }

  /**
     * Весь путь к разметке пакета — внутри :deep от корня SFC (обёртка + узлы gallery-slider).
     */
  :deep(.post__gallery-trigger-wrap--two-photos .gallery-trigger.gallery-trigger_list-opener) {
    grid-template-rows: 1fr;
    grid-template-columns: 1fr 1fr;
  }

  :deep(.post__gallery-trigger-wrap--two-photos .gallery-trigger__item_list-opener-1) {
    grid-area: 1 / 1 / 2 / 2;
    min-height: 0;
  }

  :deep(.post__gallery-trigger-wrap--two-photos .gallery-trigger__item_list-opener-2) {
    grid-area: 1 / 2 / 2 / 3;
    min-height: 0;
  }

  @include tablet {
    margin-top: 24px;
  }

  @include tablet {
    :deep(.post__gallery-trigger-wrap--two-photos .gallery-trigger.gallery-trigger_list-opener) {
      grid-template-columns: 1fr 1fr;
      grid-auto-flow: row;
    }
  }

  /* stylelint-enable selector-class-pattern */
}

.post__gallery-trigger-wrap {
  max-width: var(--width-content);
}

.post__gallery-trigger {
  max-width: 100%;
}

.post__collage {
  display: grid;
  grid-template: 1fr / repeat(2, 1fr);
  gap: 4px;
  align-items: stretch;

  img {
    display: block;
    width: 100%;
    border-radius: 2px;
  }
}

.post__collage-item {
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--alias-color-background-subtler);
  border-radius: 2px 0 0 2px;

  img {
    border-radius: 2px 0 0 2px;
  }

  & + & {
    border-radius: 0 2px 2px 0;

    img {
      border-radius: 0 2px 2px 0;
    }
  }
}

.post__collage-text-wrapper {
  max-width: var(--width-content);
}

.post__collage-text {
  max-width: var(--width-content);
  margin: var(--alias-space-12, 12px) 0 0;

  :deep(a) {
    color: var(--alias-color-link-default);
    text-decoration: none;

    &:hover {
      color: var(--alias-color-link-default-hovered);
    }
  }

  :deep(.text) {
    color: var(--alias-color-text-default);
  }

  @include base-300-300-regular;

  @include tablet {
    margin: 8px 0 0;
  }
}
</style>
